/**
 * Slice 14 — Done screen. Slice 49 / C7: when the job has both times, show the finish line,
 * Started and Finished rows, and the total in plain words. Times only: no map, no distance.
 */
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL_MIN } from "@/lib/turnoverChrome";
import { useTranslation } from "@/hooks/useTranslation";
import { formatClockTime, formatDurationWords, minutesBetween } from "@/lib/locationStamps";
import { useOfflineStatus } from "@/hooks/useOfflineStatus";

interface Props {
  photoCount?: number;
  issueCount?: number;
  /** When both are set the done screen shows the job's times (Slice 49). */
  startedAt?: string | null;
  finishedAt?: string | null;
  onJobs: () => void;
}

export function SimpleDoneStep({ photoCount = 0, issueCount = 0, startedAt, finishedAt, onJobs }: Props) {
  const { t } = useTranslation();
  const { online, waiting, photos } = useOfflineStatus();
  const total = startedAt && finishedAt ? minutesBetween(startedAt, finishedAt) : null;
  // Slice 56: finished with no signal is finished; say it plainly and count what is still to send.
  const willSend = !online || waiting > 0;

  if (startedAt && finishedAt && total != null) {
    return (
      <div className={CLEANER_SIMPLE_STEP_SHELL_MIN}>
        <div className="flex flex-1 flex-col justify-center gap-[22px] px-7">
          <div className="flex h-[84px] w-[84px] items-center justify-center rounded-full bg-[#24a89a]">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-[30px] font-bold leading-[1.2] tracking-[-0.6px] text-[#0f1720] [text-wrap:pretty]">
            {t("doneFinishedLine")
              .replace("{{time}}", formatClockTime(finishedAt))
              .replace("{{duration}}", formatDurationWords(total))}
            {willSend ? <span className="block pt-1 text-[17px] font-medium text-[#64748b]">{t("doneWillSend")}</span> : null}
          </div>
          <div className="flex flex-col border-t-[1.5px] border-[#e6edf1]">
            <div className="flex h-14 items-center justify-between border-b-[1.5px] border-[#e6edf1]">
              <div className="text-[17px] font-medium text-[#64748b]">{t("doneStartedRow")}</div>
              <div className="text-[17px] font-bold text-[#0f1720]">{formatClockTime(startedAt)}</div>
            </div>
            <div className="flex h-14 items-center justify-between border-b-[1.5px] border-[#e6edf1]">
              <div className="text-[17px] font-medium text-[#64748b]">{t("doneFinishedRow")}</div>
              <div className="text-[17px] font-bold text-[#0f1720]">{formatClockTime(finishedAt)}</div>
            </div>
          </div>
          {photos > 0 ? (
            <div className="flex items-center gap-2.5 text-[15px] font-medium text-[#64748b]">
              <span className="h-2 w-2 shrink-0 rounded-full bg-[#94a3b0]" />
              {photos === 1 ? t("waitingPhotosOne") : t("waitingPhotosMany").replace("{{n}}", String(photos))}
            </div>
          ) : null}
        </div>
        <div className={CLEANER_SIMPLE_CTA_FOOTER}>
          <button
            type="button"
            onClick={onJobs}
            className="flex h-[68px] w-full items-center justify-center rounded-[18px] border-[1.5px] border-[#e6edf1] text-[20px] font-bold text-[#15776d] hover:border-[#24a89a]"
          >
            {t("backToJobs")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={CLEANER_SIMPLE_STEP_SHELL_MIN}>
      <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6">
        <div className="flex h-[92px] w-[92px] items-center justify-center rounded-full bg-[#e2eeed]">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#15776d" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        </div>
        <div className="text-center text-[30px] font-bold tracking-[-0.6px] text-[#0f1720]">
          {t("turnoverComplete")}
        </div>
        <div className="flex gap-2.5">
          {photoCount > 0 && (
            <div className="rounded-full bg-[#f4f7f9] px-3.5 py-2 text-[14px] font-bold text-[#475569]">
              {photoCount === 1
                ? t("photoCountOne").replace("{{count}}", String(photoCount))
                : t("photoCount").replace("{{count}}", String(photoCount))}
            </div>
          )}
          {issueCount > 0 && (
            <div className="rounded-full bg-[#f4f7f9] px-3.5 py-2 text-[14px] font-bold text-[#475569]">
              {t("nIssues").replace("{{count}}", String(issueCount))}
            </div>
          )}
        </div>
      </div>
      <div className={CLEANER_SIMPLE_CTA_FOOTER}>
        <button
          type="button"
          onClick={onJobs}
          className="flex h-[68px] w-full items-center justify-center rounded-[18px] bg-[#24a89a] text-[23px] font-bold text-white hover:bg-[#1f958a]"
        >
          {t("jobs")}
        </button>
      </div>
    </div>
  );
}
